// src/planner/Sidebar.js
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ActionButton, Description, Check, fadeIn } from './plannerStyles';
import { color, font } from '../../theme/tokens';

const SidebarWrapper = styled.aside`
    width: 340px;
    height: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    position: relative;
    padding: 24px 20px;
    overflow-y: auto;
    overflow-x: hidden;
    background: ${color.surface};
    border-left: 1px solid ${color.line};

    @media (max-width: 860px) {
        width: 100%;
        height: auto;
        max-height: 60vh;
        border-left: none;
        border-top: 1px solid ${color.line};
    }
`;

const SidebarTitle = styled.h2`
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin: 0 0 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 800;
    font-size: 17px;
    letter-spacing: 0.2em;
    color: ${color.text};
    span { font-family: ${font.body}; font-weight: 400; font-size: 12px; letter-spacing: 0.16em; color: ${color.text3}; }
`;

const SidebarDivider = styled.div`
    height: 1px;
    margin: 28px 0;
    background: linear-gradient(90deg, transparent, rgba(200, 50, 62, 0.6), transparent);
`;

const EmptyNote = styled.p`
    margin: 0;
    padding: 8px 0;
    font-size: 13px;
    letter-spacing: 0.12em;
    color: ${color.text3};
`;

const SidebarEventCard = styled.div`
    position: relative;
    margin-bottom: 10px;
    background: rgba(7, 2, 5, 0.5);
    border: 1px solid ${color.line};
    transition: background-color 0.2s ease, border-color 0.2s ease;

    /* 左侧颜色条 */
    &::before {
        content: '';
        position: absolute;
        left: -1px; top: -1px; bottom: -1px; width: 2px;
        background-color: ${props => props.$color || color.text3};
    }

    &:hover {
        background: ${color.surface2};
        border-color: ${color.line2};
    }

    ${({ $isCompleted }) => $isCompleted && css`
        opacity: 0.55;
        filter: grayscale(1);
    `}
`;

const EventHeader = styled.div`
    padding: 12px 12px 12px 14px;
    cursor: pointer;
    user-select: none;
`;

const EventBody = styled.div`
    padding: 10px 12px 12px 14px;
    border-top: 1px solid ${color.line};
    animation: ${fadeIn} 0.2s ease-out;
`;

const SidebarEventTitle = styled.h4`
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: ${font.display};
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.06em;
    color: ${color.text};

    ${({ $isCompleted }) => $isCompleted && css`
        text-decoration: line-through;
        color: ${color.text3};
    `}
`;

const SidebarDate = styled.div`
    margin-top: 6px;
    font-family: ${font.mono};
    font-size: 12px;
    color: ${color.text2};
`;

const SidebarEventActions = styled.div`
    display: flex;
    gap: 6px;
    margin-top: 12px;
    flex-wrap: wrap;
`;

const ChildrenContainer = styled.div`
    margin-top: 4px;
    padding-left: 10px;
    border-left: 1px solid ${color.line2};
`;

const ChildrenTitle = styled.div`
    margin-bottom: 6px;
    font-size: 12px;
    letter-spacing: 0.2em;
    color: ${color.text3};
`;

const ChildItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid ${color.line};
    font-size: 13px;
    color: ${color.text2};

    &:last-child { border-bottom: none; }
`;

const ChildContent = styled.div`
    flex-grow: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;

    ${({ $isCompleted }) => $isCompleted && css`
        text-decoration: line-through;
        color: ${color.text3};
    `}
`;

const ChildDate = styled.span`
    font-family: ${font.mono};
    font-size: 11px;
    color: ${color.text3};
`;

const ChildActions = styled.div`
    display: flex;
    gap: 4px;
    flex-shrink: 0;
`;

const ExpandIndicator = styled.span`
    margin-left: auto;
    font-size: 10px;
    color: ${color.text3};
    transition: transform 0.2s ease;

    ${({ $isExpanded }) => $isExpanded && css`
        transform: rotate(180deg);
    `}
`;

const smallBtn = { minHeight: 24, padding: '0 6px', fontSize: 11 };

// 递归渲染子节点
function ChildNode({ event, allEvents, onEdit, onDelete, onToggleComplete, depth = 0 }) {
    const handleToggle = (e) => {
        e.stopPropagation();
        onToggleComplete(event);
    };

    return (
        <div style={{ marginLeft: depth * 10 }}>
            <ChildItem>
                <ChildContent $isCompleted={event.is_completed}>
                    <Check
                        checked={event.is_completed}
                        onChange={handleToggle}
                        aria-label={`完成：${event.title}`}
                    />
                    {event.title}
                    {event.due_date && (
                        <ChildDate>{new Date(event.due_date).toLocaleDateString()}</ChildDate>
                    )}
                </ChildContent>
                <ChildActions>
                    <ActionButton type="button" style={smallBtn} onClick={(e) => {e.stopPropagation(); onEdit(event);}}>编辑</ActionButton>
                    <ActionButton type="button" style={smallBtn} $danger onClick={(e) => {e.stopPropagation(); onDelete(event.id);}}>删除</ActionButton>
                </ChildActions>
            </ChildItem>
            
            {event.children && event.children.length > 0 && (
                <div style={{ marginTop: '5px' }}>
                    {event.children.map(child => (
                        <ChildNode 
                            key={child.id}
                            event={child}
                            allEvents={allEvents}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onToggleComplete={onToggleComplete}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function SidebarEventTree({ 
    eventTree, 
    allEvents, 
    expandedNodes, 
    onNodeExpand, 
    onEdit, 
    onDelete, 
    onAddSubtask, 
    onToggleComplete 
}) {
    const isExpanded = expandedNodes?.has(eventTree.id) || false;

    return (
        <SidebarEventCard $color={eventTree.color} $isCompleted={eventTree.is_completed}>
            <EventHeader onClick={(e) => { e.stopPropagation(); onNodeExpand(eventTree.id, !isExpanded); }}>
                <SidebarEventTitle $isCompleted={eventTree.is_completed}>
                    <Check
                        checked={eventTree.is_completed}
                        onChange={(e) => { e.stopPropagation(); onToggleComplete(eventTree); }}
                        onClick={e => e.stopPropagation()}
                        aria-label={`完成：${eventTree.title}`}
                    />
                    {eventTree.title}
                    {(eventTree.children && eventTree.children.length > 0) && (
                        <ExpandIndicator $isExpanded={isExpanded} aria-hidden="true">▼</ExpandIndicator>
                    )}
                </SidebarEventTitle>
                <Description style={{ margin: '6px 0 0 0' }}>
                    {eventTree.description || '暂无描述。'}
                </Description>
            </EventHeader>

            {isExpanded && (
                <EventBody>
                    {eventTree.children && eventTree.children.length > 0 && (
                        <ChildrenContainer>
                            <ChildrenTitle>子项</ChildrenTitle>
                            {eventTree.children.map(child => (
                                <ChildNode 
                                    key={child.id} event={child} allEvents={allEvents}
                                    onEdit={onEdit} onDelete={onDelete} onToggleComplete={onToggleComplete}
                                />
                            ))}
                        </ChildrenContainer>
                    )}

                    <SidebarEventActions>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); onEdit(eventTree); }}>编辑</ActionButton>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); onAddSubtask(eventTree); }}>添加子项</ActionButton>
                        <ActionButton type="button" $danger onClick={(e) => { e.stopPropagation(); onDelete(eventTree.id); }}>删除</ActionButton>
                    </SidebarEventActions>
                </EventBody>
            )}
        </SidebarEventCard>
    );
}

// DatedEventCard 实现与 SidebarEventTree 类似，为保持一致性
function DatedEventCard(props) {
    const { eventTree, onNodeExpand, expandedNodes, onToggleComplete } = props;
    const isExpanded = expandedNodes?.has(eventTree.id) || false;
    
    // 格式化日期
    const dateStr = new Date(eventTree.due_date).toLocaleDateString('zh-CN', {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });

    return (
        <SidebarEventCard $color={eventTree.color} $isCompleted={eventTree.is_completed}>
            <EventHeader onClick={(e) => { e.stopPropagation(); onNodeExpand(eventTree.id, !isExpanded); }}>
                <SidebarEventTitle $isCompleted={eventTree.is_completed}>
                    <Check
                        checked={eventTree.is_completed}
                        onChange={(e) => { e.stopPropagation(); onToggleComplete(eventTree); }}
                        onClick={e => e.stopPropagation()}
                        aria-label={`完成：${eventTree.title}`}
                    />
                    {eventTree.title}
                    {(eventTree.children && eventTree.children.length > 0) && <ExpandIndicator $isExpanded={isExpanded} aria-hidden="true">▼</ExpandIndicator>}
                </SidebarEventTitle>
                
                <SidebarDate>{dateStr}</SidebarDate>
                
                <Description style={{ margin: '6px 0 0 0' }}>
                    {eventTree.description || '暂无描述。'}
                </Description>
            </EventHeader>

            {isExpanded && (
                <EventBody>
                    {eventTree.children && eventTree.children.length > 0 && (
                        <ChildrenContainer>
                            <ChildrenTitle>子项</ChildrenTitle>
                            {eventTree.children.map(child => (
                                <ChildNode key={child.id} event={child} allEvents={props.allEvents} onEdit={props.onEdit} onDelete={props.onDelete} onToggleComplete={props.onToggleComplete} />
                            ))}
                        </ChildrenContainer>
                    )}
                    <SidebarEventActions>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); props.onEdit(eventTree); }}>编辑</ActionButton>
                        <ActionButton type="button" onClick={(e) => { e.stopPropagation(); props.onAddSubtask(eventTree); }}>添加子项</ActionButton>
                        <ActionButton type="button" $danger onClick={(e) => { e.stopPropagation(); props.onDelete(eventTree.id); }}>删除</ActionButton>
                    </SidebarEventActions>
                </EventBody>
            )}
        </SidebarEventCard>
    );
}

function Sidebar({ eventTrees, allEvents, expandedNodes, onNodeExpand, onEdit, onDelete, onAddSubtask, onToggleComplete }) {
    const datedEvents = useMemo(() => {
        const eventsWithDate = allEvents.filter(event => event.due_date);
        eventsWithDate.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        const eventMap = new Map(eventsWithDate.map(e => [e.id, { ...e, children: [] }]));
        const rootEvents = [];
        eventsWithDate.forEach(event => {
            const eventWithChildren = eventMap.get(event.id);
            if (event.parent_id && eventMap.has(event.parent_id)) {
                eventMap.get(event.parent_id).children.push(eventWithChildren);
            } else {
                rootEvents.push(eventWithChildren);
            }
        });
        return rootEvents;
    }, [allEvents]);

    return (
        <SidebarWrapper>
            <SidebarTitle>未定<span>未排期事项</span></SidebarTitle>
            {eventTrees && eventTrees.length > 0 ? (
                eventTrees.map(tree => (
                    <SidebarEventTree key={tree.id} eventTree={tree} allEvents={allEvents} expandedNodes={expandedNodes} onNodeExpand={onNodeExpand} onEdit={onEdit} onDelete={onDelete} onAddSubtask={onAddSubtask} onToggleComplete={onToggleComplete} />
                ))
            ) : <EmptyNote>暂无未排期事项。</EmptyNote>}

            <SidebarDivider />

            <SidebarTitle>日程<span>已排期事项</span></SidebarTitle>
            {datedEvents.length > 0 ? (
                datedEvents.map(tree => (
                    <DatedEventCard key={tree.id} eventTree={tree} allEvents={allEvents} expandedNodes={expandedNodes} onNodeExpand={onNodeExpand} onEdit={onEdit} onDelete={onDelete} onAddSubtask={onAddSubtask} onToggleComplete={onToggleComplete} />
                ))
            ) : <EmptyNote>暂无已排期事项。</EmptyNote>}
        </SidebarWrapper>
    );
}

export default Sidebar;